import { useCallback, useEffect, useState } from "react";

import icBox from "@/assets/icons/ic-folder.svg";
import icCpuChip from "@/assets/icons/ic-cpu.svg";
import icMessageSquare from "@/assets/icons/ic-chat.svg";
import icUsers from "@/assets/icons/ic-user-dash.svg";
import { useAdminPageTitle } from "@/contexts/admin-page-title-context";
import MetricsRow from "@/pages/dashboard/components/metrics-row";
import ReportsRow from "@/pages/dashboard/components/reports-row";
import type { DashboardMetric, DashboardReport, TrendDirection } from "@/pages/dashboard/types";
import { getDashboardStats } from "@/services/dashboard-service";
import type { DashboardStats } from "@/types/dashboard";

const toTrendDirection = (pct: number): TrendDirection => {
  if (pct > 0) return "up";
  if (pct < 0) return "down";
  return "neutral";
};

const formatPct = (pct: number): string => {
  const abs = Math.abs(pct);
  return pct >= 0 ? `+${abs}% 지난달 대비` : `-${abs}% 지난달 대비`;
};

const buildMetrics = (stats: DashboardStats): DashboardMetric[] => [
  {
    icon: icUsers,
    label: "총 사용자 수",
    value: stats.total_users.total.toLocaleString(),
    trend: {
      direction: toTrendDirection(stats.total_users.change_percentage),
      label: formatPct(stats.total_users.change_percentage),
    },
  },
  {
    icon: icCpuChip,
    label: "활성 에이전트",
    value: stats.active_agents.total.toLocaleString(),
    trend: {
      direction: toTrendDirection(stats.active_agents.change_percentage),
      label: formatPct(stats.active_agents.change_percentage),
    },
  },
  {
    icon: icBox,
    label: "활성 프로젝트",
    value: stats.active_projects.total.toLocaleString(),
    trend: {
      direction: toTrendDirection(stats.active_projects.change_percentage),
      label: formatPct(stats.active_projects.change_percentage),
    },
  },
  {
    icon: icMessageSquare,
    label: "총 문의 건수",
    value: stats.total_inquiries.total.toLocaleString(),
    trend: {
      direction: "neutral",
      label: `${stats.total_inquiries.awaiting_count}건 대기 중`,
    },
  },
];

const buildReports = (stats: DashboardStats): DashboardReport[] => [
  {
    title: "리포트 (수요자 측)",
    totalLabel: "총 수요자 수",
    totalValue: stats.demand_report.total_demanders,
    totalUnit: " 수요자",
    listTitle: "가장 활발한 수요자",
    items: stats.demand_report.top_demanders.map((d) => ({
      name: d.requester_name,
      value: `${d.project_count} projects`,
    })),
  },
  {
    title: "리포트 (공급사 측)",
    totalLabel: "총 공급사 수",
    totalValue: stats.supply_report.total_suppliers,
    totalUnit: " 공급사",
    listTitle: "가장 활발한 공급사",
    items: stats.supply_report.top_suppliers.map((s) => ({
      name: s.provider,
      value: `${s.agent_count} agents`,
    })),
  },
];

const DashboardPage = () => {
  const { setPageTitle } = useAdminPageTitle();
  const [metrics, setMetrics] = useState<DashboardMetric[]>([]);
  const [reports, setReports] = useState<DashboardReport[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const response = await getDashboardStats();
      if (response.success) {
        setMetrics(buildMetrics(response.data));
        setReports(buildReports(response.data));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setPageTitle("대시보드");
    fetchStats();
  }, [setPageTitle, fetchStats]);

  return (
    <>
      {loading && (
        <div className="flex items-center justify-center py-20 h-[60vh] w-full">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-black/10 border-t-[#FF3B00]" />
        </div>
      )}
      <div 
        className="flex flex-col gap-6 w-full"
        style={{ display: loading ? "none" : "flex" }}
      >
      <MetricsRow metrics={metrics} />
      <ReportsRow reports={reports} />
    </div>
    </>
  );
};

export default DashboardPage;
